import GroupForm from './group-form'
import { Icons } from '@/theme/Images'
import { Dialog, DialogContent, DialogFooter, DialogTrigger, DialogClose } from '@/components/ui/dialog'
import { useUnsavedChangesPrompt } from '@/hooks/use-unsaved-changes'
import { useState } from 'react'
import { Divider } from '@/components/divider'
import RulesDialogHeader from '../dialog-header/rules-dialog-header'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { TOASTS } from '@/data/toast-messages'
import { ToastMessage } from '@/components/toast-message'
import ConfirmationDialog from '../confirmation-dialog/confirmation-dialog'

import { initialValues, validationSchema } from './group-config'
import { useFormik } from 'formik'
import { useCreateGroup } from '../../hooks/use-rewards-group'
import { useAuth } from '@/contexts/auth.context'

const CreateGroup = () => {
  //Helps in managing unsaved changes*
  const {
    setUnsavedChanges, //sets a flag indicating whether there are unsaved changes
    showConfirmation, //displays a confirmation prompt-
    handleConfirm, //handles the confirmation--
    handleCancel, //handles the cancellation
    promptIfUnsaved, //checks if there are unsaved changes & prompts  user to confirm or cancel
  } = useUnsavedChangesPrompt(() => {
    setOpen(false)
  })

  const { user } = useAuth()

  const [groupInputFeilds, setGroupInputFeilds] = useState('')

  const [open, setOpen] = useState(false)

  const handleGroupInputChange = value => {
    setGroupInputFeilds(value)
    setUnsavedChanges(true)
  }

  const handleMaxPointsChange = () => {
    setUnsavedChanges(true)
    // Formik will handle the actual value update
  }

  const handleMaxCountChange = () => {
    setUnsavedChanges(true)
    // Formik will handle the actual value update
  }

  const { mutate: createGroup } = useCreateGroup()

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: values => {
      const transformedValues = {
        ruleGroup: values.ruleGroup.trim(),
        maxPoints: parseFloat(values.maxPoints) || null,
        maxCount: parseFloat(values.maxCount) || null,
        createdBy: user?.email,
      }

      createGroup(transformedValues, {
        onSuccess: response => {
          if (!response?.ruleGroup) {
            setOpen(false)
            console.error('Failed to create category: Missing ruleGroup in response.')
            toast.custom(() => <ToastMessage {...TOASTS.CREATE_GROUP.GROUP_CREATE_FAILURE} />)
            return
          }
          setOpen(false)
          toast.custom(() => <ToastMessage {...TOASTS.CREATE_GROUP.GROUP_CREATE_SUCCESS} />)
        },
        onError: error => {
          console.error('Error creating group:', error)
          toast.custom(() => <ToastMessage {...TOASTS.CREATE_GROUP.GROUP_CREATE_FAILURE} />)
        },
      })
    },
  })

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={isOpen => {
          if (!isOpen) {
            promptIfUnsaved() // Prompt if there are unsaved changes
          } else {
            setUnsavedChanges(false) // Reset unsaved changes if dialog is opened
            formik.resetForm()
          }
        }}
      >
        <DialogTrigger asChild>
          <Button className="bg-brand-600 hover:bg-brand-700 md:w-auto h-[36px] text-sm text-white">
            <img src={Icons.plusIcon} alt="icon" height={11.67} width={11.67} />
            <span className="font-semibold text-sm ml-2">Add Group</span>
          </Button>
        </DialogTrigger>

        <DialogContent className="max-w-[880px] h-[78vh] overflow-y-auto overflow-x-hidden">
          <RulesDialogHeader
            title="Add rewards group"
            description="Create a new rewards group to organize and prioritize reward rules."
            icon={Icons.addRewardsRule}
          />
          <div className="ml-[-26px] w-[106%]">
            <Divider />
          </div>

          <GroupForm
            groupInputs={groupInputFeilds}
            onGroupDescriptionChange={handleGroupInputChange}
            onMaxPointsChange={handleMaxPointsChange}
            onMaxCountChange={handleMaxCountChange}
            onCancel={() => setOpen(false)}
            formik={formik}
          />

          <div className="ml-[-26px] w-[106%]">
            <Divider />
          </div>

          {/* Confirmation dialog */}
          <ConfirmationDialog isOpen={showConfirmation} onClose={handleCancel} onConfirm={handleConfirm} />

          <DialogFooter>
            <DialogClose asChild>
              <Button
                variant="outline"
                className="w-full md:w-[410px] h-[44px] mb-[-6px] text-[--gray-700] mt-1"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              className="text-white w-full md:w-[410px] h-[44px] mb-[-6px] mt-1"
              onClick={() => formik.handleSubmit()}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CreateGroup
